import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Credentialed instance — used by admin endpoints (session cookie required).
// The Emergent ingress in front of the FastAPI backend adds
// `Access-Control-Allow-Origin: *` on every response. Browsers REJECT that
// combination when `withCredentials: true`, so this instance must ONLY be used
// for endpoints that genuinely need the session cookie (i.e. /admin/*).
export const api = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

// Anonymous / public instance — no cookies attached, so wildcard CORS is
// legal. Use this for every public endpoint (leads, consultations, payments,
// posts, uploads, health).
export const apiPublic = axios.create({
    baseURL: API,
    withCredentials: false,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

export async function uploadFile(file) {
    const fd = new FormData();
    fd.append('file', file);
    // credentials: 'omit' keeps the request wildcard-CORS friendly.
    const res = await fetch(`${API}/uploads`, { method: 'POST', body: fd, credentials: 'omit' });
    if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Upload failed');
    }
    return res.json();
}

export async function createConsultation(payload) {
    const { data } = await apiPublic.post('/consultations', payload);
    return data;
}

export async function createLead(payload) {
    const { data } = await apiPublic.post('/leads', payload);
    return data;
}

export async function createPaymentCheckout(payload) {
    const { data } = await apiPublic.post('/payments/checkout', payload);
    return data;
}

export async function getPaymentStatus(sessionId) {
    const { data } = await apiPublic.get(`/payments/status/${sessionId}`);
    return data;
}

export async function listPosts() {
    const { data } = await apiPublic.get('/posts');
    return data;
}

export async function getPost(slug) {
    const { data } = await apiPublic.get(`/posts/${slug}`);
    return data;
}

export async function getMe() {
    const { data } = await api.get('/auth/me');
    return data;
}

export async function logout() {
    await api.post('/auth/logout');
}

export async function authSession(sessionId) {
    const { data } = await api.post('/auth/session', { session_id: sessionId });
    return data;
}

export async function adminOverview() {
    const { data } = await api.get('/admin/overview');
    return data;
}
export async function adminListConsultations(filters = {}) {
    const { data } = await api.get('/admin/consultations', { params: filters });
    return data;
}
export async function adminUpdateConsultation(cid, payload) {
    const { data } = await api.patch(`/admin/consultations/${cid}`, payload);
    return data;
}
export async function adminListLeads() {
    const { data } = await api.get('/admin/leads');
    return data;
}
export async function adminUpdateLead(leadId, payload) {
    const { data } = await api.patch(`/admin/leads/${leadId}`, payload);
    return data;
}
export async function adminListEmailLog(limit = 50) {
    const { data } = await api.get('/admin/email-log', { params: { limit } });
    return data;
}
export async function adminEmailConfig() {
    const { data } = await api.get('/admin/email-config');
    return data;
}
export async function adminListPayments() {
    const { data } = await api.get('/admin/payments');
    return data;
}
export async function adminCreatePaymentLink(payload) {
    const { data } = await api.post('/admin/payment-links', payload);
    return data;
}
export async function adminListPosts() {
    const { data } = await api.get('/admin/posts');
    return data;
}
export async function adminCreatePost(payload) {
    const { data } = await api.post('/admin/posts', payload);
    return data;
}
export async function adminUpdatePost(postId, payload) {
    const { data } = await api.patch(`/admin/posts/${postId}`, payload);
    return data;
}
export async function adminDeletePost(postId) {
    const { data } = await api.delete(`/admin/posts/${postId}`);
    return data;
}
